import { Box, Stack, Tooltip, Typography } from '@mui/material';
import type { CheckItem, CheckStatus } from 'shared';
import { DISPLAY, MONO, tokens } from '../theme';

const MARK: Record<CheckStatus, { bg: string; glyph: string; label: string }> = {
  ok: { bg: tokens.mint, glyph: '✓', label: 'đạt' },
  warn: { bg: tokens.amber, glyph: '!', label: 'cần xem' },
  bad: { bg: tokens.rose, glyph: '×', label: 'chưa đạt' },
};

/** Dấu tròn ✓ / ! / × — khối `\.mark` của demo. */
function Mark({ status }: { status: CheckStatus }) {
  const m = MARK[status];
  return (
    <Tooltip title={m.label}>
      <Box
        sx={{
          flex: 'none',
          mt: '3px',
          width: 18,
          height: 18,
          borderRadius: '50%',
          bgcolor: m.bg,
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        {m.glyph}
      </Box>
    </Tooltip>
  );
}

const TIER_BG = { A: tokens.mint, B: tokens.amber, C: tokens.rose } as const;
const TIER_FG = { A: tokens.ink, B: '#43301A', C: '#fff' } as const;
/**
 * Câu chú giải cũ ("tự động áp dụng vào bản nháp", "vào hàng đợi duyệt") mô tả
 * Apply Engine — thứ ĐÃ RA KHỎI PHẠM VI: Copilot không ghi vào mã nguồn của ai và
 * không có hàng đợi duyệt nào. Hứa trong tooltip rồi không làm được là cách nhanh
 * nhất làm người dùng mất tin vào phần còn lại của báo cáo.
 *
 * Tier giờ chỉ nói MỨC RỦI RO khi tự tay sửa, đúng vai trò nó đang đảm nhiệm.
 */
const TIER_HINT = {
  A: 'Tier A — sửa được ngay, ít rủi ro thương hiệu',
  B: 'Tier B — nên có người rà lại trước khi đăng',
  C: 'Tier C — bắt buộc người có thẩm quyền duyệt',
} as const;

/** Viên tròn hạng tự động hoá — khối `\.tier` của demo (PRS §9). */
function TierPill({ tier }: { tier: 'A' | 'B' | 'C' }) {
  return (
    <Tooltip title={TIER_HINT[tier]}>
      <Box
        sx={{
          flex: 'none',
          width: 22,
          height: 22,
          borderRadius: '50%',
          bgcolor: TIER_BG[tier],
          color: TIER_FG[tier],
          display: 'grid',
          placeItems: 'center',
          fontFamily: DISPLAY,
          fontWeight: 700,
          fontSize: 12,
        }}
      >
        {tier}
      </Box>
    </Tooltip>
  );
}

/**
 * Danh sách mục kiểm tra — hiển thị CẢ mục đạt, không chỉ mục lỗi.
 * Người dùng cần thấy cái gì đang ổn, không phải đoán (theo mockup của BA).
 *
 * Dùng danh sách có đường phân cách thay vì bảng: mỗi mục chỉ có ba phần (dấu,
 * tên, diễn giải), dựng thành bảng thì cột "diễn giải" dài ngắn thất thường làm
 * các hàng lệch nhau.
 */
export function CheckTable({ checks }: { checks: CheckItem[] }) {
  if (checks.length === 0) {
    return (
      <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>Chưa có dữ liệu.</Typography>
    );
  }

  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {checks.map((c) => (
        <Stack
          key={c.code}
          component="li"
          direction="row"
          spacing={1.4}
          sx={{
            alignItems: 'flex-start',
            py: 1.1,
            borderBottom: `1px solid ${tokens.line2}`,
            '&:last-of-type': { borderBottom: 'none' },
          }}
        >
          <Mark status={c.status} />
          <Box flex={1} minWidth={0}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>{c.label}</Typography>
            <Typography sx={{ fontSize: 13.5, color: 'text.secondary' }}>{c.note}</Typography>
            {c.actual && (
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: 12.5,
                  color: 'text.secondary',
                  wordBreak: 'break-word',
                  mt: 0.3,
                }}
              >
                {c.actual}
              </Typography>
            )}
          </Box>
          {c.tier && <TierPill tier={c.tier} />}
        </Stack>
      ))}
    </Box>
  );
}

/** Đếm nhanh theo trạng thái, hiện ở đầu mỗi nhóm. */
export function StatusSummary({ checks }: { checks: CheckItem[] }) {
  const count = (s: CheckStatus) => checks.filter((c) => c.status === s).length;
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ my: 1.2 }}>
      {(['ok', 'warn', 'bad'] as const).map((s) => (
        <Stack key={s} direction="row" spacing={0.7} alignItems="center">
          <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: MARK[s].bg }} />
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            <Box component="span" sx={{ fontFamily: MONO, fontWeight: 600, color: 'text.primary' }}>
              {count(s)}
            </Box>{' '}
            {MARK[s].label}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}
